const DEDUPE_STORAGE_KEY = "gtm_event_dedupe_v1";
const DEFAULT_DEDUPE_TTL_MS = 60 * 60 * 1000; // 1 hour

const isBrowser = () => typeof window !== "undefined";

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed);
};

const readDedupeStore = () => {
  if (!isBrowser()) return {};

  try {
    const raw = window.sessionStorage.getItem(DEDUPE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return isPlainObject(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
};

const writeDedupeStore = (store) => {
  if (!isBrowser()) return;

  try {
    window.sessionStorage.setItem(DEDUPE_STORAGE_KEY, JSON.stringify(store));
  } catch (_) {
    // Ignore storage write errors (quota/private mode).
  }
};

const removeExpiredDedupeKeys = (store, ttlMs) => {
  const now = Date.now();
  const nextStore = { ...store };

  Object.keys(nextStore).forEach((key) => {
    const timestamp = Number(nextStore[key]);
    if (!Number.isFinite(timestamp) || now - timestamp > ttlMs) {
      delete nextStore[key];
    }
  });

  return nextStore;
};

const sanitizePayload = (value) => {
  if (Array.isArray(value)) {
    const sanitizedArray = value
      .map((entry) => sanitizePayload(entry))
      .filter((entry) => entry !== undefined);

    return sanitizedArray.length ? sanitizedArray : undefined;
  }

  if (isPlainObject(value)) {
    const sanitizedObject = Object.entries(value).reduce((acc, [key, entry]) => {
      const sanitizedEntry = sanitizePayload(entry);
      if (sanitizedEntry !== undefined) {
        acc[key] = sanitizedEntry;
      }
      return acc;
    }, {});

    return Object.keys(sanitizedObject).length ? sanitizedObject : undefined;
  }

  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === "number" && !Number.isFinite(value)) {
    return undefined;
  }

  return value;
};

export const normalizeMoneyValue = (value, precision = 2) => {
  if (!isFiniteNumber(value)) return undefined;
  const factor = 10 ** precision;
  return Math.round(Number(value) * factor) / factor;
};

export const getDataLayer = () => {
  if (!isBrowser()) return null;

  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }

  return window.dataLayer;
};

export const createEventDedupeKey = (eventName, ...parts) => {
  const normalizedParts = [eventName, ...parts]
    .map((part) => (part === null || part === undefined ? "" : String(part).trim()))
    .filter(Boolean);

  return normalizedParts.join("|");
};

export const hasRecentDedupeKey = (dedupeKey, ttlMs = DEFAULT_DEDUPE_TTL_MS) => {
  if (!dedupeKey || !isBrowser()) return false;

  const store = readDedupeStore();
  const cleanedStore = removeExpiredDedupeKeys(store, ttlMs);

  if (JSON.stringify(store) !== JSON.stringify(cleanedStore)) {
    writeDedupeStore(cleanedStore);
  }

  const timestamp = Number(cleanedStore[dedupeKey]);
  if (!Number.isFinite(timestamp)) return false;

  return Date.now() - timestamp <= ttlMs;
};

export const setDedupeKey = (dedupeKey) => {
  if (!dedupeKey || !isBrowser()) return;

  const store = readDedupeStore();
  const nextStore = {
    ...store,
    [dedupeKey]: Date.now(),
  };

  writeDedupeStore(nextStore);
};

export const pushDataLayerEvent = (eventName, payload = {}, options = {}) => {
  if (!eventName || typeof eventName !== "string") {
    return { pushed: false, reason: "invalid_event_name" };
  }

  const dataLayer = getDataLayer();
  if (!dataLayer) {
    return { pushed: false, reason: "data_layer_unavailable" };
  }

  const {
    dedupeKey = "",
    dedupeTtlMs = DEFAULT_DEDUPE_TTL_MS,
  } = options;

  if (dedupeKey && hasRecentDedupeKey(dedupeKey, dedupeTtlMs)) {
    return { pushed: false, reason: "deduped" };
  }

  const sanitizedPayload = sanitizePayload(payload) || {};
  const eventPayload = {
    event: eventName,
    ...sanitizedPayload,
  };

  dataLayer.push(eventPayload);

  if (dedupeKey) {
    setDedupeKey(dedupeKey);
  }

  return { pushed: true, reason: "ok", payload: eventPayload };
};

